/**
 * Phase 38 — the hit-again drop's amount, from the action and two facts:
 * what the drop that hit sent and the container's base amount. One
 * function for the engine, the judge and the editor's mirror, so the words
 * in the feed and on the card are the same arithmetic.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_HIT_LIMITS, type DropGenHitAction } from "./hit-again.types";

const n = (v: number): string => v.toLocaleString("en-US");

const signed = (pct: number): string => `${pct >= 0 ? "+" : "−"}${Math.abs(pct)} %`;

/** The amount before the cap, and the words that say how it was made. */
function asked(action: DropGenHitAction, facts: { drop: number; base: number }): { amount: number; note: string } {
  switch (action.mode) {
    case "same":
      return { amount: facts.drop, note: `same as the drop (${n(facts.drop)})` };
    case "drop_pct":
      return { amount: Math.round(facts.drop * (1 + action.pct / 100)), note: `${signed(action.pct)} of the drop (${n(facts.drop)})` };
    case "base_pct":
      return { amount: Math.round(facts.base * (1 + action.pct / 100)), note: `${signed(action.pct)} of the base (${n(facts.base)})` };
    case "fixed":
      return { amount: action.fixed ?? facts.drop, note: action.fixed === null ? `same as the drop (${n(facts.drop)})` : `fixed ${n(action.fixed)}` };
  }
}

/** The new drop's amount (never under 1, never over the cap) and its note — "+25 % of the drop (1,000) → 1,250". */
export function hitAmount(action: DropGenHitAction, facts: { drop: number; base: number }): { amount: number; note: string } {
  const a = asked(action, facts);
  const capped = action.cap !== null && a.amount > action.cap;
  const amount = Math.max(DROP_GEN_HIT_LIMITS.amount.min, capped ? action.cap! : a.amount);
  return { amount, note: `${a.note} → ${n(amount)}${capped ? " (capped)" : ""}` };
}

/** How many steps the ladder shows for a chain with no limit (it goes on while it hits). */
export const HIT_LADDER_OPEN_STEPS = 3;

/** The ladder the editor shows: what one drop's chain sends, hit after hit — its first
 *  HIT_LADDER_OPEN_STEPS when the chain has no limit. */
export function hitLadder(action: DropGenHitAction, facts: { drop: number; base: number }): number[] {
  const out: number[] = [];
  let drop = facts.drop;
  for (let k = 0; k < (action.maxTimes ?? HIT_LADDER_OPEN_STEPS); k += 1) {
    drop = hitAmount(action, { drop, base: facts.base }).amount;
    out.push(drop);
  }
  return out;
}
